import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { X } from 'lucide-react'
import type { HubItem, HubItemInput, Category } from '@/types'
import { CATEGORIES, CATEGORY_ORDER } from '@/lib/categories'
import { cn } from '@/lib/utils'

interface Props {
  initial?: HubItem
  onSave: (data: HubItemInput) => Promise<void>
  onClose: () => void
}

export default function ItemForm({ initial, onSave, onClose }: Props) {
  const { register, handleSubmit, watch, setValue, formState: { errors, isSubmitting } } = useForm<HubItemInput>({
    defaultValues: initial
      ? {
          category: initial.category,
          subcategory: initial.subcategory,
          title: initial.title,
          provider: initial.provider,
          reference_number: initial.reference_number,
          amount: initial.amount,
          amount_frequency: initial.amount_frequency,
          start_date: initial.start_date,
          expiry_date: initial.expiry_date,
          renewal_reminder_days: initial.renewal_reminder_days,
          notes: initial.notes,
          metadata: initial.metadata,
        }
      : { category: 'insurance', renewal_reminder_days: 30, metadata: {} },
  })

  const category = watch('category') as Category
  const subcategory = watch('subcategory')
  const catConfig = CATEGORIES[category]
  const subKeys = Object.keys(catConfig?.subcategories ?? {})
  const subConfig = catConfig?.subcategories[subcategory]

  // Reset subcategory when category changes
  useEffect(() => {
    if (!initial) setValue('subcategory', subKeys[0] ?? '')
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category])

  async function onSubmit(data: HubItemInput) {
    await onSave(data)
    onClose()
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-end md:items-center justify-center p-0 md:p-4">
      <div className="bg-white w-full md:max-w-lg md:rounded-2xl rounded-t-2xl max-h-[90dvh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-4 border-b border-slate-100">
          <h2 className="text-base font-semibold text-slate-900">
            {initial ? 'Edit Record' : 'New Record'}
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit(onSubmit)} className="overflow-auto flex-1 p-4 space-y-4">

          {/* Category */}
          <div>
            <label className="label">Category</label>
            <select className="input" {...register('category', { required: true })}>
              {CATEGORY_ORDER.map(cat => (
                <option key={cat} value={cat}>{CATEGORIES[cat].label}</option>
              ))}
            </select>
          </div>

          {/* Subcategory */}
          <div>
            <label className="label">Type</label>
            <select className="input" {...register('subcategory', { required: true })}>
              {subKeys.map(key => (
                <option key={key} value={key}>{catConfig.subcategories[key].label}</option>
              ))}
            </select>
          </div>

          {/* Title */}
          <div>
            <label className="label">Title <span className="text-red-500">*</span></label>
            <input
              className={cn('input', errors.title && 'border-red-400')}
              placeholder="e.g. NRMA Comprehensive — RAV4"
              {...register('title', { required: true })}
            />
          </div>

          {/* Dynamic metadata fields */}
          {subConfig?.fields.map(field => (
            <div key={field.key}>
              <label className="label">{field.label}</label>
              {field.type === 'select' ? (
                <select
                  className="input"
                  {...register(field.metadataField ? `metadata.${field.key}` as never : field.key as keyof HubItemInput)}
                >
                  <option value="">Select…</option>
                  {field.options?.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>
              ) : (
                <input
                  className="input"
                  type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
                  placeholder={field.placeholder}
                  step={field.type === 'number' ? 'any' : undefined}
                  {...register(field.metadataField ? `metadata.${field.key}` as never : field.key as keyof HubItemInput)}
                />
              )}
            </div>
          ))}

          <hr className="border-slate-100" />

          {/* Provider */}
          <div>
            <label className="label">Provider / Issuer</label>
            <input className="input" placeholder="e.g. NRMA, ANZ, City of Sydney Council" {...register('provider')} />
          </div>

          {/* Reference number */}
          <div>
            <label className="label">Policy / Reference Number</label>
            <input className="input" placeholder="Policy #, rego #, loan account #…" {...register('reference_number')} />
          </div>

          {/* Amount + frequency */}
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="label">Amount (AUD)</label>
              <input className="input" type="number" step="any" placeholder="0.00" {...register('amount', { valueAsNumber: true })} />
            </div>
            <div className="w-36">
              <label className="label">Frequency</label>
              <select className="input" {...register('amount_frequency')}>
                <option value="">—</option>
                <option value="annual">Annual</option>
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="fortnightly">Fortnightly</option>
                <option value="weekly">Weekly</option>
                <option value="once">Once</option>
              </select>
            </div>
          </div>

          {/* Dates */}
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="label">Start Date</label>
              <input className="input" type="date" {...register('start_date')} />
            </div>
            <div className="flex-1">
              <label className="label">Expiry / Due Date</label>
              <input className="input" type="date" {...register('expiry_date')} />
            </div>
          </div>

          {/* Reminder */}
          <div>
            <label className="label">Remind me (days before expiry)</label>
            <input className="input" type="number" min="0" {...register('renewal_reminder_days', { valueAsNumber: true })} />
          </div>

          {/* Notes */}
          <div>
            <label className="label">Notes</label>
            <textarea className="input resize-none" rows={3} placeholder="Any extra details…" {...register('notes')} />
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full btn-primary py-3 text-base font-semibold"
          >
            {isSubmitting ? 'Saving…' : initial ? 'Save Changes' : 'Add Record'}
          </button>
        </form>
      </div>
    </div>
  )
}
